// import React, { useEffect, useMemo, useRef, useState } from 'react';
// import { InputNumber, Table, Radio, Button, Tag, message } from 'antd';
// import {
//   ShopOutlined,
//   ThunderboltOutlined,
//   UndoOutlined,
//   WarningFilled,
//   CheckCircleFilled,
//   CalendarOutlined
// } from '@ant-design/icons';
// import ModalComponent from '@components/common/Modal';
// import DataManagementApi from 'apis/api/DataManagement';
// import Helpers from 'helpers/Helpers';

// interface IRow {
//   id: any;
//   lineNo: number;
//   productName: string;
//   unit: string;
//   quantity: number;
//   oldPrice: number;
//   price: number;
// }

// interface IProps {
//   isOpen: boolean;
//   rowSelect: any;
//   allRows?: any[];
//   callBackClose: () => void;
//   callBackRefresh: () => void;
// }

// const money = (v: number) => Helpers.FormatMoney(v || 0, true);
// const priceFormatter = (v: any) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
// const priceParser = (v: any) => (v ? v.replace(/,/g, '') : 0) as any;

// export default function UpdatePriceReturn({ isOpen, rowSelect, allRows = [], callBackClose, callBackRefresh }: IProps) {
//   const [rows, setRows] = useState<IRow[]>([]);
//   const [fillValue, setFillValue] = useState<number>(0);
//   const [fillMode, setFillMode] = useState<'empty' | 'all'>('empty');
//   const [loading, setLoading] = useState(false);

//   const inputRefs = useRef<Record<string, any>>({});

//   /* ------------------------------------------------------------------ */
//   /* Khởi tạo: lấy toàn bộ dòng cùng voucherNo                           */
//   /* ------------------------------------------------------------------ */
//   useEffect(() => {
//     if (!isOpen) return;

//     const list = allRows.filter((r: any) => r.voucherNo && r.voucherNo === rowSelect?.voucherNo);
//     const source = list.length ? list : rowSelect ? [rowSelect] : [];

//     setRows(
//       source
//         .map((r: any) => ({
//           id: r.id,
//           lineNo: r.lineNo || 0,
//           productName: r.productName || '',
//           unit: r.unit || '',
//           quantity: r.quantity || 0,
//           oldPrice: r.unitPrice || 0,
//           price: r.unitPrice || 0
//         }))
//         .sort((a, b) => a.lineNo - b.lineNo)
//     );
//     setFillValue(0);
//     setFillMode('empty');
//   }, [isOpen, rowSelect, allRows]);

//   /* ------------------------------------------------------------------ */
//   /* Derived state                                                       */
//   /* ------------------------------------------------------------------ */
//   const changed = useMemo(() => rows.filter(r => r.price !== r.oldPrice), [rows]);
//   const emptyCount = useMemo(() => rows.filter(r => !r.oldPrice).length, [rows]);
//   const oldTotal = useMemo(() => rows.reduce((s, r) => s + r.oldPrice * r.quantity, 0), [rows]);
//   const newTotal = useMemo(() => rows.reduce((s, r) => s + r.price * r.quantity, 0), [rows]);
//   const diff = newTotal - oldTotal;

//   /* ------------------------------------------------------------------ */
//   /* Handlers                                                            */
//   /* ------------------------------------------------------------------ */
//   const setPrice = (id: any, v: any) =>
//     setRows(prev => prev.map(r => (r.id === id ? { ...r, price: Number(v) || 0 } : r)));

//   const applyFill = () => {
//     if (fillValue < 0) return message.error('Đơn giá không hợp lệ');
//     setRows(prev => prev.map(r => (fillMode === 'empty' && r.oldPrice > 0 ? r : { ...r, price: fillValue })));
//   };

//   const resetAll = () => setRows(prev => prev.map(r => ({ ...r, price: r.oldPrice })));

//   const focusNext = (index: number) => {
//     const next = rows[index + 1];
//     if (next) inputRefs.current[next.id]?.focus();
//   };

//   const handleSubmit = async () => {
//     if (!changed.length) {
//       message.warning('Chưa có dòng nào thay đổi đơn giá');
//       return;
//     }
//     if (changed.some(r => r.price < 0 || Number.isNaN(r.price))) {
//       message.error('Đơn giá không hợp lệ');
//       return;
//     }

//     setLoading(true);
//     try {
//       const res = await DataManagementApi.UpdateReturnProduct({
//         items: changed.map(r => ({ returnItemId: r.id, unitPrice: r.price }))
//       });

//       if (!res?.data?.error) {
//         message.success(`Cập nhật ${changed.length} dòng thành công`);
//         callBackClose();
//         callBackRefresh();
//       } else {
//         message.error(res?.data?.message || 'Cập nhật đơn giá thất bại');
//       }
//     } catch (error) {
//       console.log(error);
//       message.error('Cập nhật đơn giá thất bại');
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ------------------------------------------------------------------ */
//   /* Columns                                                             */
//   /* ------------------------------------------------------------------ */
//   const columns = useMemo(
//     () => [
//       {
//         title: '#',
//         dataIndex: 'lineNo',
//         width: 48,
//         align: 'center' as const,
//         render: (v: number) => <span className="text-xs text-gray-400 tabular-nums">{v}</span>
//       },
//       {
//         title: 'Tên hàng',
//         dataIndex: 'productName',
//         ellipsis: true,
//         render: (v: string) => (
//           <div className="truncate font-medium text-gray-800" title={v}>
//             {v || '-'}
//           </div>
//         )
//       },
//       {
//         title: 'SL',
//         dataIndex: 'quantity',
//         width: 90,
//         align: 'right' as const,
//         render: (v: number, r: IRow) => (
//           <span className="tabular-nums text-gray-700">
//             {v}
//             {r.unit && <span className="ml-1 text-xs text-gray-400">{r.unit}</span>}
//           </span>
//         )
//       },
//       {
//         title: 'Đơn giá cũ',
//         dataIndex: 'oldPrice',
//         width: 120,
//         align: 'right' as const,
//         render: (v: number, r: IRow) =>
//           v > 0 ? (
//             <span className={`tabular-nums ${r.price !== v ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
//               {money(v)}
//             </span>
//           ) : (
//             <span className="text-gray-300">—</span>
//           )
//       },
//       {
//         title: 'Đơn giá mới',
//         dataIndex: 'price',
//         width: 160,
//         align: 'right' as const,
//         render: (_: any, r: IRow, index: number) => (
//           <InputNumber
//             ref={(el: any) => (inputRefs.current[r.id] = el)}
//             aria-label={`Đơn giá mới dòng ${r.lineNo}`}
//             className="w-full tabular-nums no-spin"
//             style={r.price !== r.oldPrice ? { borderColor: '#40a9ff', background: '#f0f7ff' } : undefined}
//             min={0}
//             value={r.price}
//             onChange={(v: any) => setPrice(r.id, v)}
//             onPressEnter={() => focusNext(index)}
//             formatter={priceFormatter}
//             parser={priceParser}
//           />
//         )
//       },
//       {
//         title: 'Thành tiền',
//         width: 140,
//         align: 'right' as const,
//         render: (_: any, r: IRow) => {
//           const lineDiff = (r.price - r.oldPrice) * r.quantity;
//           return (
//             <div className="tabular-nums leading-tight">
//               <div className={lineDiff ? 'font-semibold text-gray-900' : 'text-gray-700'}>
//                 {money(r.price * r.quantity)}
//               </div>
//               {lineDiff !== 0 && (
//                 <div className={`text-xs ${lineDiff > 0 ? 'text-green-600' : 'text-red-500'}`}>
//                   {lineDiff > 0 ? '+' : '−'}
//                   {money(Math.abs(lineDiff))}
//                 </div>
//               )}
//             </div>
//           );
//         }
//       }
//     ],
//     [rows]
//   );

//   /* ------------------------------------------------------------------ */
//   /* Render                                                              */
//   /* ------------------------------------------------------------------ */
//   return (
//     <ModalComponent
//       title={
//         <div className="flex items-center gap-3">
//           <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-lg text-blue-600">
//             <ShopOutlined rev={''} aria-hidden />
//           </span>
//           <div className="min-w-0 leading-tight">
//             <div className="text-base font-semibold text-gray-900">Cập nhật đơn giá</div>
//             <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs font-normal text-gray-500">
//               <span>
//                 Phiếu trả hàng <span className="font-semibold text-gray-700">{rowSelect?.voucherNo || '-'}</span>
//               </span>
//               {rowSelect?.returnDateExport && (
//                 <span className="flex items-center gap-1">
//                   <CalendarOutlined rev={''} aria-hidden />
//                   {rowSelect.returnDateExport}
//                 </span>
//               )}
//             </div>
//           </div>
//         </div>
//       }
//       visible={isOpen}
//       onCancel={callBackClose}
//       maskClosable={false}
//       destroyOnClose
//       width={960}
//       style={{ top: 20 }}
//       footer={
//         <div className="flex flex-wrap items-center justify-between gap-2">
//           <div className="text-left text-sm text-gray-500">
//             {changed.length ? (
//               <>
//                 Đã sửa <span className="font-semibold text-gray-800">{changed.length}</span>/{rows.length} dòng
//                 <span className="mx-1.5 text-gray-300">|</span>
//                 Chênh lệch{' '}
//                 <span className={`font-semibold tabular-nums ${diff >= 0 ? 'text-green-600' : 'text-red-500'}`}>
//                   {diff >= 0 ? '+' : '−'}
//                   {money(Math.abs(diff))} ₫
//                 </span>
//               </>
//             ) : (
//               'Nhập đơn giá mới, Enter để sang dòng kế tiếp'
//             )}
//           </div>
//           <div className="flex gap-2">
//             <Button onClick={callBackClose}>Đóng</Button>
//             <Button type="primary" loading={loading} disabled={!changed.length} onClick={handleSubmit}>
//               {changed.length ? `Cập nhật (${changed.length})` : 'Cập nhật'}
//             </Button>
//           </div>
//         </div>
//       }
//     >
//       <div className="space-y-4">
//         {/* Số liệu tổng quan */}
//         <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
//           <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
//             <div className="text-xs text-gray-500">Số dòng</div>
//             <div className="mt-1 text-xl font-semibold tabular-nums text-gray-900">{rows.length}</div>
//           </div>
//           <div
//             className={`rounded-lg border px-3 py-2.5 ${
//               emptyCount ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'
//             }`}
//           >
//             <div className="text-xs text-gray-600">Chưa có giá</div>
//             <div
//               className={`mt-1 flex items-center gap-1.5 text-xl font-semibold tabular-nums ${
//                 emptyCount ? 'text-red-600' : 'text-green-700'
//               }`}
//             >
//               {emptyCount ? (
//                 <WarningFilled rev={''} className="text-base" aria-hidden />
//               ) : (
//                 <CheckCircleFilled rev={''} className="text-base" aria-hidden />
//               )}
//               {emptyCount ? emptyCount : 'Đủ giá'}
//             </div>
//           </div>
//           <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
//             <div className="text-xs text-gray-500">Tổng tiền cũ</div>
//             <div className="mt-1 text-xl font-semibold tabular-nums text-gray-700">{money(oldTotal)} ₫</div>
//           </div>
//           <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
//             <div className="text-xs text-gray-600">Tổng tiền mới</div>
//             <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
//               <span className="text-xl font-semibold tabular-nums text-blue-700">{money(newTotal)} ₫</span>
//               {diff !== 0 && (
//                 <span className={`text-xs font-medium tabular-nums ${diff > 0 ? 'text-green-600' : 'text-red-500'}`}>
//                   {diff > 0 ? '+' : '−'}
//                   {money(Math.abs(diff))}
//                 </span>
//               )}
//             </div>
//           </div>
//         </div>

//         {/* Điền nhanh */}
//         <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
//           <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
//             <ThunderboltOutlined rev={''} className="text-yellow-500" aria-hidden />
//             Điền nhanh đơn giá
//           </div>
//           <InputNumber
//             aria-label="Đơn giá điền nhanh"
//             className="tabular-nums no-spin"
//             min={0}
//             style={{ width: 160 }}
//             value={fillValue}
//             onChange={(v: any) => setFillValue(Number(v) || 0)}
//             formatter={priceFormatter}
//             parser={priceParser}
//             // addonAfter="₫"
//           />
//           <Radio.Group
//             value={fillMode}
//             onChange={e => setFillMode(e.target.value)}
//             optionType="button"
//             buttonStyle="solid"
//           >
//             <Radio.Button value="empty">Dòng chưa có giá ({emptyCount})</Radio.Button>
//             <Radio.Button value="all">Tất cả ({rows.length})</Radio.Button>
//           </Radio.Group>
//           <Button type="primary" ghost onClick={applyFill}>
//             Áp dụng
//           </Button>
//           <Button
//             className="ml-auto"
//             type="text"
//             icon={<UndoOutlined rev={''} />}
//             onClick={resetAll}
//             disabled={!changed.length}
//           >
//             Hoàn tác
//           </Button>
//         </div>

//         {/* Bảng chi tiết */}
//         <Table
//           rowKey="id"
//           size="small"
//           columns={columns as any}
//           dataSource={rows}
//           pagination={false}
//           scroll={{ y: 360 }}
//           className="rounded-lg border border-gray-200 overflow-hidden"
//           rowClassName={(r: IRow) => (r.price !== r.oldPrice ? 'bg-blue-50' : '')}
//         />
//       </div>
//     </ModalComponent>
//   );
// }

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InputNumber, Table, Radio, Button, Tag, message, Modal } from 'antd';
import {
  ShopOutlined,
  ThunderboltOutlined,
  UndoOutlined,
  WarningFilled,
  CheckCircleFilled,
  CalendarOutlined,
  EditOutlined
} from '@ant-design/icons';
import ModalComponent from '@components/common/Modal';
import DataManagementApi from 'apis/api/DataManagement';
import Helpers from 'helpers/Helpers';

interface IRow {
  id: string | number;
  lineNo: number;
  productName: string;
  unit: string;
  quantity: number;
  oldPrice: number;
  price: number;
}

interface IProps {
  isOpen: boolean;
  rowSelect: any;
  allRows?: any[];
  callBackClose: () => void;
  callBackRefresh: () => void;
}

type FillMode = 'empty' | 'all';

const money = (value: number) => Helpers.FormatMoney(value || 0, true);
const priceFormatter = (value: any) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const priceParser = (value: any) => (value ? value.replace(/,/g, '') : 0) as any;

export default function UpdatePriceReturn({ isOpen, rowSelect, allRows = [], callBackClose, callBackRefresh }: IProps) {
  const [rows, setRows] = useState<IRow[]>([]);
  const [fillValue, setFillValue] = useState<number | null>(null);
  const [fillMode, setFillMode] = useState<FillMode>('empty');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<Record<string, any>>({});

  useEffect(() => {
    if (!isOpen) return;

    const matchingRows = allRows.filter((row: any) => row.voucherNo && row.voucherNo === rowSelect?.voucherNo);
    const source = matchingRows.length ? matchingRows : rowSelect ? [rowSelect] : [];

    setRows(
      source
        .map((row: any) => {
          const unitPrice = Number(row.unitPrice) || 0;

          return {
            id: row.id,
            lineNo: Number(row.lineNo) || 0,
            productName: row.productName || '',
            unit: row.unit || '',
            quantity: Number(row.quantity) || 0,
            oldPrice: unitPrice,
            price: unitPrice
          };
        })
        .sort((a, b) => a.lineNo - b.lineNo)
    );
    setFillValue(null);
    setFillMode('empty');
    inputRefs.current = {};
  }, [isOpen, rowSelect, allRows]);

  const changed = useMemo(() => rows.filter(row => Number(row.price) !== Number(row.oldPrice)), [rows]);

  // "Chưa có giá" phản ánh giá hiện tại, không phải chỉ giá gốc.
  const emptyCount = useMemo(() => rows.filter(row => row.price <= 0).length, [rows]);
  const oldTotal = useMemo(() => rows.reduce((sum, row) => sum + row.oldPrice * row.quantity, 0), [rows]);
  const newTotal = useMemo(() => rows.reduce((sum, row) => sum + row.price * row.quantity, 0), [rows]);
  const diff = newTotal - oldTotal;

  const setPrice = (id: IRow['id'], value: number | null) => {
    setRows(previous =>
      previous.map(row => (row.id === id ? { ...row, price: Math.max(0, Number(value) || 0) } : row))
    );
  };

  const applyFill = () => {
    if (fillValue === null || !Number.isFinite(fillValue) || fillValue < 0) {
      message.error('Vui lòng nhập đơn giá hợp lệ');
      return;
    }

    const applyToRows = (previous: IRow[]) =>
      previous.map(row => {
        if (fillMode === 'empty' && row.price > 0) return row;
        return { ...row, price: fillValue };
      });

    if (fillMode === 'all') {
      Modal.confirm({
        title: 'Áp dụng đơn giá cho tất cả sản phẩm?',
        content: 'Đơn giá hiện tại của tất cả dòng sẽ được thay bằng đơn giá bạn vừa nhập.',
        okText: 'Áp dụng',
        cancelText: 'Hủy',
        okButtonProps: { type: 'primary' },
        onOk: () => {
          setRows(applyToRows);
          message.success('Đã áp dụng đơn giá cho tất cả dòng');
        }
      });
      return;
    }

    setRows(applyToRows);
    message.success('Đã áp dụng cho các dòng chưa có giá');
  };

  const resetAll = () => {
    setRows(previous => previous.map(row => ({ ...row, price: row.oldPrice })));
    message.info('Đã hoàn tác các thay đổi đơn giá');
  };

  const focusNext = (index: number) => {
    const nextRow = rows[index + 1];
    if (nextRow) inputRefs.current[String(nextRow.id)]?.focus?.();
  };

  const handleSubmit = async () => {
    if (!changed.length) {
      message.warning('Chưa có dòng nào thay đổi đơn giá');
      return;
    }

    if (changed.some(row => !Number.isFinite(row.price) || row.price < 0)) {
      message.error('Đơn giá không hợp lệ');
      return;
    }

    setLoading(true);
    try {
      const response = await DataManagementApi.UpdateReturnProduct({
        items: changed.map(row => ({
          returnItemId: row.id,
          unitPrice: row.price
        }))
      });

      if (!response?.data?.error) {
        message.success(`Cập nhật ${changed.length} dòng thành công`);
        callBackClose();
        callBackRefresh();
      } else {
        message.error(response?.data?.message || 'Cập nhật đơn giá thất bại');
      }
    } catch (error) {
      console.error(error);
      message.error('Cập nhật đơn giá thất bại');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'lineNo',
        width: 54,
        align: 'center' as const,
        render: (value: number) => <span className="text-xs tabular-nums text-gray-500">{value}</span>
      },
      {
        title: 'Tên sản phẩm',
        dataIndex: 'productName',
        ellipsis: true,
        render: (value: string, row: IRow) => (
          <div className="min-w-0">
            <div className="truncate font-medium text-gray-800" title={value}>
              {value || '—'}
            </div>
            {row.price !== row.oldPrice && <div className="mt-0.5 text-[11px] text-blue-600">Đã thay đổi đơn giá</div>}
          </div>
        )
      },
      {
        title: 'Số lượng',
        dataIndex: 'quantity',
        width: 100,
        align: 'right' as const,
        render: (value: number, row: IRow) => (
          <span className="whitespace-nowrap tabular-nums text-gray-700">
            {money(value)}
            {row.unit && <span className="ml-1 text-xs text-gray-400">{row.unit}</span>}
          </span>
        )
      },
      {
        title: 'Đơn giá cũ',
        dataIndex: 'oldPrice',
        width: 132,
        align: 'right' as const,
        render: (value: number, row: IRow) =>
          value > 0 ? (
            <span
              className={`whitespace-nowrap tabular-nums ${
                row.price !== value ? 'text-gray-400 line-through' : 'text-gray-600'
              }`}
            >
              {money(value)}
            </span>
          ) : (
            <Tag className="mr-0 border-gray-200 bg-gray-50 text-gray-500">Chưa có giá</Tag>
          )
      },
      {
        title: 'Đơn giá mới',
        dataIndex: 'price',
        width: 164,
        align: 'right' as const,
        render: (_: number, row: IRow, index: number) => {
          const isChanged = row.price !== row.oldPrice;

          return (
            <InputNumber
              ref={(element: any) => {
                inputRefs.current[String(row.id)] = element;
              }}
              aria-label={`Đơn giá mới dòng ${row.lineNo}`}
              className="w-full tabular-nums"
              size="middle"
              min={0}
              precision={0}
              value={row.price}
              onChange={(value: number | null) => setPrice(row.id, value)}
              onPressEnter={() => focusNext(index)}
              formatter={priceFormatter}
              parser={priceParser}
              style={{
                width: '100%',
                borderColor: isChanged ? '#91caff' : undefined,
                background: isChanged ? '#f0f7ff' : '#fff',
                textAlign: 'right'
              }}
            />
          );
        }
      },
      {
        title: 'Thành tiền',
        key: 'amount',
        width: 152,
        align: 'right' as const,
        render: (_: unknown, row: IRow) => {
          const lineDiff = (row.price - row.oldPrice) * row.quantity;

          return (
            <div className="tabular-nums leading-tight">
              <div className={`font-medium ${lineDiff ? 'text-gray-900' : 'text-gray-700'}`}>
                {money(row.price * row.quantity)}
              </div>
              {lineDiff !== 0 && (
                <div className={`mt-1 text-xs font-medium ${lineDiff > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {lineDiff > 0 ? '+' : '−'}
                  {money(Math.abs(lineDiff))}
                </div>
              )}
            </div>
          );
        }
      }
    ],
    [rows]
  );

  return (
    <ModalComponent
      title={
        <div className="flex min-w-0 items-center gap-3 py-0.5">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-blue-600">
            <ShopOutlined rev={''} aria-hidden />
          </span>
          <div className="min-w-0 leading-tight">
            <div className="text-base font-semibold text-gray-900">Cập nhật đơn giá</div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-normal text-gray-500">
              <span>
                Phiếu trả hàng <span className="font-semibold text-gray-800">{rowSelect?.voucherNo || '—'}</span>
              </span>
              {rowSelect?.returnDateExport && (
                <span className="flex items-center gap-1">
                  <CalendarOutlined rev={''} aria-hidden />
                  {rowSelect.returnDateExport}
                </span>
              )}
            </div>
          </div>
        </div>
      }
      visible={isOpen}
      onCancel={callBackClose}
      maskClosable={false}
      destroyOnClose
      width={1080}
      style={{ top: 16, maxWidth: 'calc(100vw - 24px)' }}
      bodyStyle={{ padding: '16px 20px' }}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 text-left text-sm">
            {changed.length ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-gray-600">
                  Đã sửa <strong className="text-gray-900">{changed.length}</strong>/{rows.length} dòng
                </span>
                <span className="hidden text-gray-300 sm:inline">|</span>
                <span className="text-gray-600">
                  Chênh lệch{' '}
                  <strong
                    className={`tabular-nums ${
                      diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-500' : 'text-gray-700'
                    }`}
                  >
                    {diff > 0 ? '+' : diff < 0 ? '−' : ''}
                    {money(Math.abs(diff))} ₫
                  </strong>
                </span>
              </div>
            ) : (
              <span className="text-gray-500">Nhập đơn giá mới, nhấn Enter để sang dòng kế tiếp.</span>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={callBackClose} disabled={loading}>
              Đóng
            </Button>
            <Button
              type="primary"
              loading={loading}
              disabled={!changed.length}
              onClick={handleSubmit}
              icon={<EditOutlined rev={''} />}
            >
              {changed.length ? `Cập nhật (${changed.length})` : 'Cập nhật'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3.5">
        {/* Tổng quan */}
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white px-3.5 py-3">
            <div className="text-xs font-medium text-gray-500">Số dòng sản phẩm</div>
            <div className="mt-1.5 text-xl font-semibold leading-none tabular-nums text-gray-900">{rows.length}</div>
          </div>

          <div
            className={`rounded-lg border px-3.5 py-3 ${
              emptyCount ? 'border-red-200 bg-red-50/50' : 'border-gray-200 bg-white'
            }`}
          >
            <div className="text-xs font-medium text-gray-500">Chưa có đơn giá</div>
            <div
              className={`mt-1.5 flex items-center gap-1.5 text-xl font-semibold leading-none tabular-nums ${
                emptyCount ? 'text-red-600' : 'text-green-700'
              }`}
            >
              {emptyCount ? (
                <WarningFilled rev={''} className="text-sm" aria-hidden />
              ) : (
                <CheckCircleFilled rev={''} className="text-sm" aria-hidden />
              )}
              {emptyCount || 'Đủ giá'}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white px-3.5 py-3">
            <div className="text-xs font-medium text-gray-500">Tổng tiền cũ</div>
            <div className="mt-1.5 truncate text-lg font-semibold leading-none tabular-nums text-gray-700">
              {money(oldTotal)} <span className="text-xs font-normal">₫</span>
            </div>
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50/50 px-3.5 py-3">
            <div className="text-xs font-medium text-gray-600">Tổng tiền mới</div>
            <div className="mt-1.5 truncate text-lg font-semibold leading-none tabular-nums text-blue-700">
              {money(newTotal)} <span className="text-xs font-normal">₫</span>
            </div>
            {diff !== 0 && (
              <div
                className={`mt-1.5 text-xs font-medium tabular-nums ${diff > 0 ? 'text-green-600' : 'text-red-500'}`}
              >
                {diff > 0 ? '+' : '−'}
                {money(Math.abs(diff))} ₫ so với giá cũ
              </div>
            )}
          </div>
        </div>

        {/* Thanh công cụ điền nhanh */}
        <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
            <ThunderboltOutlined rev={''} className="text-blue-600" aria-hidden />
            Điền nhanh đơn giá
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <InputNumber
              aria-label="Đơn giá điền nhanh"
              className="tabular-nums"
              size="middle"
              min={0}
              precision={0}
              placeholder="Nhập đơn giá"
              style={{ width: 170 }}
              value={fillValue}
              onChange={(value: number | null) => setFillValue(value)}
              formatter={priceFormatter}
              parser={priceParser}
              addonAfter="₫"
            />
            <Radio.Group
              value={fillMode}
              onChange={event => setFillMode(event.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="middle"
            >
              <Radio.Button value="empty">Dòng chưa có giá ({emptyCount})</Radio.Button>
              <Radio.Button value="all">Tất cả ({rows.length})</Radio.Button>
            </Radio.Group>
            <Button type="primary" ghost onClick={applyFill}>
              Áp dụng
            </Button>
            <Button
              className="sm:ml-auto"
              type="text"
              icon={<UndoOutlined rev={''} />}
              onClick={resetAll}
              disabled={!changed.length || loading}
            >
              Hoàn tác
            </Button>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            {fillMode === 'empty'
              ? 'Chỉ áp dụng cho những dòng hiện chưa có đơn giá.'
              : 'Đơn giá đã nhập ở tất cả các dòng sẽ bị thay thế.'}
          </div>
        </div>

        {/* Bảng chi tiết */}
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <div className="flex items-center justify-between border-b border-gray-200 bg-white px-3.5 py-2.5">
            <div className="text-sm font-semibold text-gray-800">Chi tiết sản phẩm</div>
            <div className="text-xs text-gray-500">
              {rows.length} sản phẩm
              {changed.length > 0 && (
                <span className="ml-2 font-medium text-blue-600">· {changed.length} dòng đã sửa</span>
              )}
            </div>
          </div>
          <Table
            rowKey="id"
            size="small"
            columns={columns as any}
            dataSource={rows}
            pagination={false}
            tableLayout="fixed"
            scroll={{ y: 340, x: 760 }}
            rowClassName={(row: IRow) => (row.price !== row.oldPrice ? 'update-price-row-changed' : '')}
            locale={{
              emptyText: <div className="py-8 text-sm text-gray-500">Không có sản phẩm trong phiếu này.</div>
            }}
          />
        </div>

        <style>{`
          .update-price-row-changed > td {
            background: #f7fbff !important;
          }
          .update-price-row-changed:hover > td {
            background: #edf6ff !important;
          }
          .ant-table-thead > tr > th {
            background: #f8fafc !important;
            color: #475569 !important;
            font-size: 12px !important;
            font-weight: 600 !important;
            border-bottom: 1px solid #e2e8f0 !important;
          }
          .ant-table-tbody > tr > td {
            padding-top: 8px !important;
            padding-bottom: 8px !important;
            border-bottom-color: #f1f5f9 !important;
          }
          .no-spin input::-webkit-outer-spin-button,
          .no-spin input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
        `}</style>
      </div>
    </ModalComponent>
  );
}
